import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { IQuestionnaireQuestion } from 'src/app/interfaces/Questionnaire/IQuestionnaire';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { PermissionService } from 'src/app/services/permission.service';

const DN4_POSITIVE_CUTOFF = 4;

@Component({
  selector: 'app-dn4-questions',
  templateUrl: './dn4-questions.component.html',
  styleUrl: './dn4-questions.component.css',
  standalone: false,
})
export class Dn4QuestionsComponent implements OnInit, OnDestroy {
  @Input() elements: IQuestionnaireQuestion[] = [];
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;

  totalScore = 0;

  private sub?: Subscription;

  constructor(
    private readonly eocService: EpisodeOfCareService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly permissionService: PermissionService,
  ) {}

  ngOnInit(): void {
    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.recalculateTotal();
    });
    this.recalculateTotal();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  isInpatientView(): boolean {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('inPatientView')
    );
  }

  isEOC(): boolean {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('episodeOfCare')
    );
  }

  hasPermission(permission: string[]): boolean {
    return this.permissionService.can(permission);
  }

  onRadioClick(event: Event, controlName: string, value: any): void {
    if (this.readonlyMode) return;
    const control = this.formGroup.get(controlName);
    if (!control) return;

    if (control.value === value) {
      // nz-radio-button has its own click handler that re-selects the
      // button right after we clear it (it reads the control's checked
      // state after our handler runs). Stop it from running so the
      // deselect actually sticks.
      event.stopImmediatePropagation();
      event.preventDefault();
      control.setValue(null);
    }
  }

  getQuestionTitle(title?: string): string {
    return title?.split('||')[0]?.trim() ?? '';
  }

  getQuestionInstruction(title?: string): string {
    return title?.split('||')[1]?.trim() ?? '';
  }

  get scorableElements(): IQuestionnaireQuestion[] {
    return (this.elements ?? []).filter(
      (el) => el.type === 'boolean' || el.type === 'radiogroup',
    );
  }

  get maxScore(): number {
    return this.scorableElements.length;
  }

  get isPositive(): boolean {
    return this.totalScore >= DN4_POSITIVE_CUTOFF;
  }

  questionNumber(element: IQuestionnaireQuestion): number {
    return (
      this.scorableElements.findIndex((el) => el.name === element.name) + 1
    );
  }

  private recalculateTotal(): void {
    let total = 0;

    for (const el of this.scorableElements) {
      const selectedValue = this.formGroup.get(el.name)?.value;
      if (selectedValue === null || selectedValue === undefined) continue;

      if (el.type === 'boolean') {
        if (selectedValue === true) total += 1;
        continue;
      }

      const choice = (el.choices ?? []).find(
        (c) => c.value === selectedValue,
      );
      if (choice && this.isYes(choice.text)) {
        total += 1;
      }
    }

    this.totalScore = total;
    this.cdr.detectChanges();
  }

  private norm(value: unknown): string {
    return String(value ?? '')
      .trim()
      .toLowerCase();
  }

  private isYes(text: string): boolean {
    const normalized = this.norm(this.translate.instant(text) ?? text);
    return normalized === 'yes' || normalized === 'ναι';
  }
}